import React, { useState, useEffect } from "react";
import { FaCheck, FaRegEdit, FaRegEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TableSkeleton from "../../TableSkeleton";

const BlogTable = ({
  data,
  searchTerm,
  itemsPerPage = 10,
  handleEdit,
  handleAdd,
  resetFlag,
  openDeleteModal,
  selectedLanguage,
  isLoading = false,
}) => {
  const [selectedRows, setSelectedRows] = useState(
    new Array(data.length).fill(false)
  );
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  // Skeleton columns configuration
  const skeletonColumns = [
    { width: 40, height: 16, isCircle: false, className: "pl-3" }, // Checkbox
    { width: 60, height: 16, className: "pl-3" }, // Serial Number
    { width: 80, height: 16, className: "pl-3" }, // Blog ID
    { width: 200, height: 16, className: "pl-3" }, // Title
    { width: 300, height: 16, className: "pl-3" }, // Description
    { width: 150, height: 16, className: "pl-3" }, // Category
    { width: 120, height: 16, className: "text-center" }, // Actions
  ];

  // Update header checkbox state based on the current page's rows
  useEffect(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentIndices = Array.from(
      { length: filterBlogs().length },
      (_, i) => i
    ).slice(indexOfFirstItem, indexOfLastItem);
    const isAllSelected =
      currentIndices.length > 0 && currentIndices.every((i) => selectedRows[i]);
    setIsHeaderChecked(isAllSelected);
  }, [selectedRows, currentPage, data, searchTerm]);

  // Reset selectedRows when data or resetFlag changes
  useEffect(() => {
    setSelectedRows(new Array(data.length).fill(false));
    setIsHeaderChecked(false);
  }, [resetFlag, data.length]);

  // Handle header checkbox change (toggle current page rows only)
  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentIndices = Array.from(
      { length: filterBlogs().length },
      (_, i) => i
    ).slice(indexOfFirstItem, indexOfLastItem);
    currentIndices.forEach((index) => {
      newSelectedRows[index] = !isHeaderChecked;
    });
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!isHeaderChecked);
  };

  // Handle individual row checkbox change
  const handleRowCheckboxChange = (index) => {
    const globalIndex = (currentPage - 1) * itemsPerPage + index;
    const newSelectedRows = [...selectedRows];
    newSelectedRows[globalIndex] = !newSelectedRows[globalIndex];
    setSelectedRows(newSelectedRows);
  };

  // Filter blogs based on search term and selected language
  const filterBlogs = () => {
    return data.filter((blog) => {
      const title =
        blog.translations[selectedLanguage]?.title?.toLowerCase() || "";
      const description =
        blog.translations[selectedLanguage]?.description?.toLowerCase() || "";
      return (
        title.includes(searchTerm.toLowerCase()) ||
        description.includes(searchTerm.toLowerCase())
      );
    });
  };

  // Calculate the total number of pages
  const totalPages = Math.ceil(filterBlogs().length / itemsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Slice the data based on the current page and itemsPerPage
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filterBlogs().slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3 flex-1">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Blog List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedBlogIds = data
                    .map((blog, index) =>
                      selectedRows[index] ? blog.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedBlogIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <Link
            to="/add-blog"
            className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
            onClick={handleAdd}
          >
            + Add Blog
          </Link>
        </div>

        {/* Table Area */}
        <div className="overflow-x-auto">
          <table className="min-w-[1500px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="px-3 bg-[#FBFBFD]">
                <th className="text-left">
                  <div className="relative left-3 -top-[3px]">
                    <input
                      type="checkbox"
                      id="blog-header"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="blog-header"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Serial No
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Blog ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Title
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Description
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Category
                </th>
                <th className="text-center uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                />
              ) : (
                currentData.map((blog, index) => {
                  const blogsData = blog.translations?.[currentLanguage] || {};
                  const singleBlogLink = blogsData.slug
                    ? `https://muktihospital.com/blog/${blogsData.slug}`
                    : "#";
                  const globalIndex = (currentPage - 1) * itemsPerPage + index;
                  return (
                    <tr
                      key={blog.id}
                      className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                    >
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={"blog" + globalIndex}
                            className="hidden peer"
                            checked={selectedRows[globalIndex]}
                            onChange={() => handleRowCheckboxChange(index)}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={"blog" + globalIndex}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="p-3 text-sm">{indexOfFirstItem + index + 1}</td>
                      <td className="p-3 text-sm">{blog.id}</td>
                      <td className="p-3 text-sm">
                        {blog.translations[selectedLanguage]?.title ||
                          "No Title"}
                      </td>
                      <td className="p-3 text-sm line-clamp-3">
                         <span className="w-96 inline-block">
                            {blog.translations[selectedLanguage]?.description ||
                          "No Description"}
                         </span>
                      </td>
                      <td className="p-3 text-sm">
                        {blog.translations[selectedLanguage]?.category?.name ||
                          "Uncategorized"}
                      </td>
                      <td className="text-center p-3">
                        <div className="flex justify-center gap-2">
                          <Link
                            to={singleBlogLink}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          >
                            <FaRegEye />
                          </Link>
                          <button
                            onClick={() => handleEdit(blog.id)}
                            className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                          >
                            <FaRegEdit />
                          </button>
                          <button
                            onClick={() => openDeleteModal([blog.id])}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Area */}
        {totalPages > 1 && (
          <div className="p-5 flex justify-end print-hidden">
            <div className="inline-flex items-center justify-between w-full">
               <p className="text-sm text-gray-600">
                  Records: {filterBlogs().length > 0 ? currentPage * itemsPerPage - itemsPerPage + 1 : 0} to{" "}
                  {Math.min(currentPage * itemsPerPage, filterBlogs().length)} of {filterBlogs().length}
                </p>
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
              {/* Previous Button */}
              <li>
                <button
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
              </li>

              {/* Pagination Items */}
              {totalPages <= 10
                ? // Show all pages if totalPages <= 10
                  [...Array(totalPages)].map((_, i) => (
                    <li key={i}>
                      <button
                        className={`table-pagination-item ${
                          currentPage === i + 1
                            ? "bg-M-primary-color text-white"
                            : ""
                        }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))
                : // Show first 5, ellipsis, and last 3 if totalPages > 10
                  (() => {
                    const pages = [];
                    const firstPages = 5;
                    const lastPages = 3;

                    // Always show first 5 pages
                    for (
                      let i = 1;
                      i <= Math.min(firstPages, totalPages);
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    // Add ellipsis if there are pages between first 5 and last 3
                    if (totalPages > firstPages + lastPages) {
                      pages.push(
                        <li key="ellipsis" className="table-pagination-item">
                          ...
                        </li>
                      );
                    }

                    // Show last 3 pages
                    for (
                      let i = Math.max(
                        firstPages + 1,
                        totalPages - lastPages + 1
                      );
                      i <= totalPages;
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}

              {/* Next Button */}
              <li>
                <button
                  className="table-pagination-item"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <MdKeyboardArrowRight />
                </button>
              </li>
            </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogTable;